import styled from 'styled-components';
import { updateObjectNestedProperties } from '../../../../functions/launcherElement/updateObjectNestedProperties';
import { LauncherElementData, useLauncherElementsStore } from '../../../../hooks/stores/launcherElementsStore';
import { LauncherWindowData, useLauncherWindowStore } from '../../../../hooks/stores/launcherWindowStore';
import { UIData, useUIStore } from '../../../../hooks/stores/uiStore';
import { IconSelectInput } from '../../baseInputs/IconSelectInput';
import { useScene } from '../../../../context/sceneContext';

export const PositioningInput = ({ forWindow }: { forWindow?: boolean }) => {
  const scene = useScene();
  const [launcherWindowData, setLauncherWindowData] = useLauncherWindowStore<LauncherWindowData>();
  const [launcherElementsData] = useLauncherElementsStore<Record<string, LauncherElementData>>();
  const [UIData] = useUIStore<UIData>({ selector: ['selectedElementIDs'] });
  const selectedElement = launcherElementsData[UIData.selectedElementIDs?.slice(-1)[0]];
  const selectedElementType = selectedElement?.type;

  const updateValue = (key: string, value?: string | number) => {
    if (forWindow) {
      setLauncherWindowData(updateObjectNestedProperties(launcherWindowData, { [key]: value }, 'class'));
      return;
    }

    scene.updateSelectedElementsData(
      { [key]: value, display: selectedElementType === 'popup' ? 'none' : 'flex', textOverflow: undefined },
      'props.class',
      'positioningInput'
    );
  };

  const data = forWindow
    ? (launcherWindowData.class as CSSStyleDeclaration)
    : scene.getSelectedElementsData(
        {
          display: undefined,
          flexDirection: 'row',
          justifyContent: undefined,
          alignItems: undefined,
        },
        'props.class'
      );

  return (
    <Row style={{ padding: '4px 15px' }}>
      <Row>
        <IconSelectInput
          value={data.flexDirection}
          defaultValue="row"
          data={[
            {
              value: 'row',
              iconClassName: 'icon-row',
              iconStyle: { fontSize: '13px' },
              id: 'position-row',
              tooltip: 'Row',
            },
            {
              value: 'column',
              iconClassName: 'icon-row',
              iconStyle: { transform: 'rotate(90deg)', fontSize: '13px' },
              id: 'position-column',
              tooltip: 'Column',
            },
          ]}
          onChange={(newValue) => updateValue('flexDirection', newValue)}
        />
      </Row>
      <Row style={{ flexWrap: 'wrap' }}>
        <Row>
          <IconSelectInput
            value={data.flexDirection === 'row' ? data.justifyContent : data.alignItems}
            defaultValue={data.display === 'flex' ? 'flex-start' : undefined}
            data={[
              {
                value: 'flex-start',
                iconClassName: 'icon-positioning-left',
                iconStyle: { fontSize: '13px' },
                id: 'position-horizontal-left',
                tooltip: 'Horizontal Left',
              },
              {
                value: 'center',
                iconClassName: 'icon-positioning-center',
                iconStyle: { fontSize: '13px' },
                id: 'position-horizontal-center',
                tooltip: 'Horizontal Center',
              },
              {
                value: 'flex-end',
                iconClassName: 'icon-positioning-left',
                iconStyle: { transform: 'rotate(180deg)', fontSize: '13px' },
                id: 'position-horizontal-right',
                tooltip: 'Horizontal Right',
              },
              ...(data.flexDirection === 'row' || data.flexDirection === undefined
                ? [
                    {
                      value: 'space-between',
                      iconClassName: 'icon-positioning-space-between',
                      iconStyle: {
                        fontSize: '13px',
                      },
                      id: 'position-horizontal-space-between',
                      tooltip: 'Vertical Space Between',
                    },
                    {
                      value: 'space-around',
                      iconClassName: 'icon-positioning-space-around',
                      iconStyle: {
                        fontSize: '13px',
                      },
                      id: 'position-horizontal-space-around',
                      tooltip: 'Vertical Space Around',
                    },
                  ]
                : []),
            ]}
            onChange={(newValue) =>
              updateValue(data.flexDirection === 'row' ? 'justifyContent' : 'alignItems', newValue)
            }
          />
        </Row>
        <Row>
          <IconSelectInput
            value={data.flexDirection === 'row' ? data.alignItems : data.justifyContent}
            defaultValue={data.display === 'flex' ? 'flex-start' : undefined}
            data={[
              {
                value: 'flex-start',
                iconClassName: 'icon-positioning-left',
                iconStyle: { transform: 'rotate(90deg)', fontSize: '13px' },
                id: 'position-vertical-left',
                tooltip: 'Vertical Top',
              },
              {
                value: 'center',
                iconClassName: 'icon-positioning-center',
                iconStyle: { transform: 'rotate(90deg)', fontSize: '13px' },
                id: 'position-vertical-center',
                tooltip: 'Vertical Center',
              },
              {
                value: 'flex-end',
                iconClassName: 'icon-positioning-left',
                iconStyle: { transform: 'rotate(-90deg)', fontSize: '13px' },
                id: 'position-vertical-right',
                tooltip: 'Vertical Bottom',
              },
              ...(data.flexDirection === 'column'
                ? [
                    {
                      value: 'space-between',
                      iconClassName: 'icon-positioning-space-between',
                      iconStyle: {
                        transform: 'rotate(-90deg)',
                        fontSize: '13px',
                      },
                      id: 'position-vertical-space-between',
                      tooltip: 'Vertical Space Between',
                    },
                    {
                      value: 'space-around',
                      iconClassName: 'icon-positioning-space-around',
                      iconStyle: {
                        transform: 'rotate(-90deg)',
                        fontSize: '13px',
                      },
                      id: 'position-vertical-space-around',
                      tooltip: 'Vertical Space Around',
                    },
                  ]
                : []),
            ]}
            onChange={(newValue) =>
              updateValue(data.flexDirection === 'row' ? 'alignItems' : 'justifyContent', newValue)
            }
          />
        </Row>
      </Row>
    </Row>
  );
};

const Row = styled.div`
  box-sizing: border-box;
  width: 100%;
  display: flex;
  gap: 5px;
`;
